// ComplianceTrendSparkline — 30-day violation trend in the Manager
// Ops hero. Pure SVG, no chart lib. Auto-scales; falls back to a flat
// baseline when there's zero data so the widget never renders "broken".
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { TrendingUp, TrendingDown, Minus, ShieldAlert, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TREND_STYLE = {
  up: { icon: TrendingUp, tone: 'text-rose-600 bg-rose-50 border-rose-200', label: 'Trending up' },
  down: { icon: TrendingDown, tone: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: 'Trending down' },
  flat: { icon: Minus, tone: 'text-slate-600 bg-slate-50 border-slate-200', label: 'Flat' },
};

export const ComplianceTrendSparkline = ({ days = 30 }) => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/manager/compliance-trend?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token, days]);

  const points = useMemo(() => {
    if (!data?.series?.length) return { path: '', max: 1, area: '' };
    const counts = data.series.map((p) => p.count);
    const max = Math.max(1, ...counts);
    const W = 160;
    const H = 32;
    const step = W / Math.max(1, counts.length - 1);
    const coords = counts.map((c, i) => {
      const x = i * step;
      const y = H - (c / max) * H;
      return [x, y];
    });
    const path = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    const area = `${path} L${(coords[coords.length - 1][0]).toFixed(1)},${H} L0,${H} Z`;
    return { path, area, max };
  }, [data]);

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-2 text-slate-400 text-xs" data-testid="compliance-trend-loading">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading compliance trend…
      </div>
    );
  }
  if (!data) return null;

  const trend = TREND_STYLE[data.trend] || TREND_STYLE.flat;
  const Icon = trend.icon;
  const strokeColor = data.trend === 'up' ? '#e11d48' : data.trend === 'down' ? '#059669' : '#64748b';

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-4" data-testid="compliance-trend-widget">
      <div className="flex items-center gap-2">
        <ShieldAlert className="w-5 h-5 text-rose-500" />
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
            Compliance breaches · {data.days}d
          </div>
          <div className="text-lg font-bold text-slate-900 leading-tight" data-testid="compliance-trend-total">
            {data.total.toLocaleString()}
          </div>
        </div>
      </div>
      <svg viewBox="0 0 160 32" width="160" height="32" className="shrink-0" aria-hidden="true">
        <path d={points.area} fill={strokeColor} opacity="0.12" />
        <path d={points.path} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${trend.tone}`}
        data-testid="compliance-trend-chip"
      >
        <Icon className="w-3 h-3" />
        {data.delta_pct > 0 ? '+' : ''}{data.delta_pct}%
      </span>
    </div>
  );
};

export default ComplianceTrendSparkline;
